import { onAuthStateChanged, signOut, User } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import React, { createContext, lazy, Suspense, useContext, useEffect, useState } from "react";
import { auth, checkUserAdmin, db } from "../firebaseConfig";
import { CURRENT_TERMS_VERSION } from "../legal/versions";
import { requiresTermsAcceptance as checkTermsAcceptance, UserProfile } from "../utils/termsService";

interface AuthContextType {
  user: User | null;
  isAdmin: boolean;
  loading: boolean;
  signOut: () => Promise<void>;
}

// Only users who still need to accept the terms ever download the modal
const TermsAcceptanceModal = lazy(() => import("../components/user/TermsAcceptanceModal"));

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAdmin: false,
  loading: true,
  signOut: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [requiresTermsAcceptance, setRequiresTermsAcceptance] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setLoading(true);

      if (user) {
        try {
          // Check if user is admin
          const adminStatus = await checkUserAdmin(user);
          setIsAdmin(adminStatus);

          // Admins are exempt from T&C requirement
          if (adminStatus) {
            setRequiresTermsAcceptance(false);
          } else {
            // Check if non-admin user needs to accept T&C
            const userRef = doc(db, "users", user.uid);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
              const userProfile = userSnap.data() as UserProfile;
              setRequiresTermsAcceptance(
                checkTermsAcceptance(userProfile, CURRENT_TERMS_VERSION)
              );
            } else {
              // User profile doesn't exist yet, they'll need to accept
              setRequiresTermsAcceptance(true);
            }
          }
        } catch (error) {
          console.error("Error checking user status:", error);
          setIsAdmin(false);
          setRequiresTermsAcceptance(true);
        }
      } else {
        setIsAdmin(false);
        setRequiresTermsAcceptance(false);
      }

      setUser(user);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const signOutUser = () => signOut(auth);

  return (
    <AuthContext.Provider
      value={{ user, isAdmin, loading, signOut: signOutUser }}
    >
      {/* Public pages render immediately; anything that needs the session
          waits on `loading` itself (see ProtectedRoute). */}
      {children}
      {user && requiresTermsAcceptance && !isAdmin && (
        <Suspense fallback={null}>
          <TermsAcceptanceModal onAccepted={() => setRequiresTermsAcceptance(false)} />
        </Suspense>
      )}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
